import type { AxisScale } from '@visx/xychart';

import type { XScaleType } from '../../types/Scale';

import { BandSnapStrategy, PointSnapStrategy } from './strategies';
import type { SnapStrategy } from './types';

/**
 * Gets the bandwidth from a scale if it's a band scale.
 */
function getBandwidth(xScale: AxisScale): number {
  const scaleWithBandwidth = xScale as unknown as { bandwidth?: () => number };
  if (typeof scaleWithBandwidth.bandwidth === 'function') {
    return scaleWithBandwidth.bandwidth();
  }
  return 0;
}

/**
 * Factory for creating snap strategies based on scale type.
 *
 * Strategy selection is based on xScaleType:
 * - 'band': Uses BandSnapStrategy (snap within band width)
 * - 'time' | 'linear': Uses PointSnapStrategy (snap to nearest point)
 *
 * Crosshair visibility is controlled by showCrosshairOnDataPoints:
 * - true: Crosshair shows on data points and reference lines (default for LineChart)
 * - false: Crosshair only shows on reference lines (default for ColumnChart)
 *
 * @param xScaleType - The scale type ('time', 'linear', 'band')
 * @param xScale - The x-axis scale from visx
 * @param showCrosshairOnDataPoints - Whether to show crosshair on data points
 * @returns The appropriate snap strategy for the scale type
 */
export function createSnapStrategy(
  xScaleType: XScaleType,
  xScale: AxisScale,
  showCrosshairOnDataPoints: boolean,
): SnapStrategy {
  const bandwidth = getBandwidth(xScale);

  if (xScaleType === 'band') {
    return new BandSnapStrategy(bandwidth, showCrosshairOnDataPoints);
  }

  // For continuous scales (time, linear)
  return new PointSnapStrategy(showCrosshairOnDataPoints);
}
